const STORAGE_KEY = 'theme:geo-location-dismissed';
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

function isDismissed() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return false;

  try {
    const { timestamp } = JSON.parse(raw);
    return Date.now() - timestamp < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

function setDismissed() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ timestamp: Date.now() }));
}

function escapeAttr(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function updateLocalization({ country, language }) {
  const formId = crypto.randomUUID();
  const returnTo = window.location.pathname + window.location.search;
  const safeFormId = escapeAttr(formId);
  const safeCountry = escapeAttr(country);
  const safeReturnTo = escapeAttr(returnTo);
  const languageField = language ? `<input name="locale_code" value="${escapeAttr(language)}" />` : '';

  document.body.insertAdjacentHTML(
    'beforeend',
    `<form id="${safeFormId}" action="${window.Shopify.routes.root}localization" method="POST" hidden>
      <input name="_method" value="PUT" />
      <input name="country_code" value="${safeCountry}" />
      <input name="return_to" value="${safeReturnTo}" />
      ${languageField}
    </form>`,
  );
  document.getElementById(formId).submit();
}

function normalizeCode(code) {
  return (code || '').toLowerCase();
}

function countryCodesMatch(a, b) {
  return normalizeCode(a) === normalizeCode(b);
}

/**
 * Shows the modal only when Shopify detects a different country than the one
 * the visitor is browsing. Uses Liquid-rendered country (theme editor "View as")
 * instead of window.Shopify.country.
 */
async function shouldShowGeoModal(localizationData) {
  const currentCountry = localizationData?.currentCountry;

  if (!currentCountry) {
    return { show: false, reason: 'no_current_country' };
  }

  const params = new URLSearchParams({
    'country[enabled]': 'true',
    'country[exclude]': currentCountry,
  });

  const apiUrl = `${window.Shopify.routes.root}browsing_context_suggestions.json?${params.toString()}`;
  const response = await fetch(apiUrl);

  if (!response.ok) {
    return { show: false, reason: 'api_error', status: response.status };
  }

  const data = await response.json();

  const detectedCountry = data.detected_values?.country?.handle;
  const suggestedCountry = data.suggestions?.[0]?.parts?.country?.handle;
  const suggestedLanguage = data.suggestions?.[0]?.parts?.language?.handle;

  const inferredCountry = suggestedCountry && !countryCodesMatch(suggestedCountry, currentCountry) ? suggestedCountry : detectedCountry;

  if (!inferredCountry || countryCodesMatch(inferredCountry, currentCountry)) {
    return {
      show: false,
      reason: 'country_match',
      detectedCountry,
      suggestedCountry,
      inferredCountry,
    };
  }

  return {
    show: true,
    country: inferredCountry,
    language: suggestedLanguage,
    reason: 'country_mismatch',
  };
}

function isStorefrontThemePreview() {
  return window.location.search.includes('preview_theme_id=');
}

function registerGeoLocationModal(Modal) {
  if (window.customElements.get('geo-location-modal')) return;

  class GeoLocationModalElement extends Modal {
    connectedCallback() {
      super.connectedCallback();

      const overlay = this.getShadowPartByName('overlay');
      if (overlay) {
        overlay.replaceWith(overlay.cloneNode(true));
      }

      this.#localizationData = this.#readLocalizationData();
      this.#bindControls();
      this.#bindPopoverScroll();
      this.#init();
    }

    get shouldAppendToBody() {
      return false;
    }

    get clickOutsideDeactivates() {
      return false;
    }

    get shouldTrapFocus() {
      return true;
    }

    get escapeDeactivates() {
      return true;
    }

    #localizationData = null;
    #selectedCountry = null;
    #selectedLanguage = null;
    #countryTrigger = this.querySelector('[data-geo-trigger="country"]');
    #languageTrigger = this.querySelector('[data-geo-trigger="language"]');
    #countryTriggerLabel = this.#countryTrigger?.querySelector('[data-geo-trigger-label]');
    #countryTriggerFlagSlot = this.#countryTrigger?.querySelector('[data-geo-trigger-flag]');
    #languageTriggerLabel = this.#languageTrigger?.querySelector('[data-geo-trigger-label]');
    #languageList = this.querySelector('[data-geo-language-list]');
    #acceptButton = this.querySelector('.geo-location-modal__accept');

    #readLocalizationData() {
      const dataElement = document.querySelector('[data-geo-localization]');

      if (!dataElement) return null;

      try {
        return JSON.parse(dataElement.textContent);
      } catch {
        return null;
      }
    }

    #getCountryData(isoCode) {
      return this.#localizationData?.countries?.find((country) => country.isoCode === isoCode);
    }

    #bindPopoverScroll() {
      const mobileQuery = window.matchMedia('(max-width: 999px)');

      this.querySelectorAll('.geo-location-modal__popover').forEach((popover) => {
        const getSelectorParent = () => this.querySelector(`[aria-controls="${popover.id}"]`)?.closest('.geo-location-modal__selector');

        popover.addEventListener('dialog:after-show', () => {
          if (mobileQuery.matches) {
            const selector = getSelectorParent();
            if (selector) {
              selector.append(popover);
            }
          }

          const scrollContainer = popover.getShadowPartByName('body');

          scrollContainer?.addEventListener('wheel', (event) => event.stopPropagation(), { passive: true });
        });
      });
    }

    #bindControls() {
      this.addEventListener('click', (event) => {
        const countryButton = event.target.closest('[data-geo-country]');
        const languageButton = event.target.closest('[data-geo-language]');

        if (countryButton) {
          event.preventDefault();
          this.#setCountry(countryButton.getAttribute('data-geo-country'), null, { useMarketDefaultLanguage: true });
          this.#hidePopoverForTrigger(this.#countryTrigger);
        }

        if (languageButton) {
          event.preventDefault();
          this.#setLanguage(languageButton.getAttribute('data-geo-language'));
          this.#hidePopoverForTrigger(this.#languageTrigger);
        }
      });

      this.#acceptButton?.addEventListener('click', () => this.#onAccept());
      this.addEventListener('dialog:after-hide', () => {
        if (!this.#accepted) {
          setDismissed();
        }
      });
    }

    #accepted = false;

    #matchLanguageCode(languageCode, languages) {
      if (!languageCode || !languages?.length) return null;

      const normalizedCode = languageCode.toLowerCase();

      return languages.find((language) => {
        const optionCode = language.isoCode.toLowerCase();
        return optionCode === normalizedCode || optionCode.startsWith(`${normalizedCode}-`) || normalizedCode.startsWith(`${optionCode}-`);
      });
    }

    #getCountryLabel(country) {
      if (!country) return '';

      const symbol = country.currencySymbol ? ` ${country.currencySymbol}` : '';
      return `${country.name} (${country.currencyIso}${symbol})`;
    }

    #renderCountryFlag(countryData) {
      const slot = this.#countryTriggerFlagSlot;
      if (!slot) return;

      slot.replaceChildren();

      if (countryData?.flagUrl) {
        const img = document.createElement('img');
        img.className = 'country-flag';
        img.src = countryData.flagUrl;
        img.alt = '';
        img.width = 30;
        img.height = 20;
        img.loading = 'lazy';
        slot.appendChild(img);
        return;
      }

      const placeholder = document.createElement('span');
      placeholder.className = 'country-flag country-flag--placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      slot.appendChild(placeholder);
    }

    #hidePopoverForTrigger(trigger) {
      const popoverId = trigger?.getAttribute('aria-controls');

      if (!popoverId) return;

      const popover = document.getElementById(popoverId);

      if (popover && typeof popover.hide === 'function') {
        popover.hide();
      }
    }

    #getMarketDefaultLanguageIso(countryData, languages) {
      if (countryData?.defaultLanguageIso) {
        return countryData.defaultLanguageIso;
      }

      return languages[0]?.isoCode ?? null;
    }

    #resolveLanguageForCountry(countryData, languages, preferredLanguage, useMarketDefaultLanguage) {
      const marketDefault = this.#getMarketDefaultLanguageIso(countryData, languages);

      if (useMarketDefaultLanguage) {
        return marketDefault;
      }

      if (preferredLanguage) {
        return this.#matchLanguageCode(preferredLanguage, languages)?.isoCode ?? marketDefault;
      }

      return this.#matchLanguageCode(this.#selectedLanguage, languages)?.isoCode ?? marketDefault;
    }

    #setCountry(countryIso, preferredLanguage, { useMarketDefaultLanguage = false } = {}) {
      const countryData = this.#getCountryData(countryIso);
      if (!countryData) return;

      this.#selectedCountry = countryIso;

      if (this.#countryTriggerLabel) {
        this.#countryTriggerLabel.textContent = this.#getCountryLabel(countryData);
      }

      this.#renderCountryFlag(countryData);

      this.querySelectorAll('[data-geo-country]').forEach((button) => {
        button.setAttribute('aria-selected', button.getAttribute('data-geo-country') === countryIso ? 'true' : 'false');
      });

      const languages = countryData.languages ?? [];
      const languageToSelect = this.#resolveLanguageForCountry(
        countryData,
        languages,
        preferredLanguage,
        useMarketDefaultLanguage,
      );

      this.#updateLanguageList(languages, languageToSelect);
    }

    #setLanguage(languageIso) {
      if (!languageIso) return;

      this.#selectedLanguage = languageIso;

      if (this.#languageTriggerLabel) {
        const languageData = this.#getCountryData(this.#selectedCountry)?.languages?.find((language) => language.isoCode === languageIso);
        this.#languageTriggerLabel.textContent = languageData?.name ?? languageIso;
      }

      this.querySelectorAll('[data-geo-language]').forEach((button) => {
        button.setAttribute('aria-selected', button.getAttribute('data-geo-language') === languageIso ? 'true' : 'false');
      });

      if (this.#languageTrigger) {
        this.#languageTrigger.disabled = false;
      }
    }

    #updateLanguageList(languages, preferredLanguage) {
      if (!this.#languageList) return;

      this.#languageList.replaceChildren();

      if (!languages.length) {
        this.#selectedLanguage = null;
        if (this.#languageTrigger) {
          this.#languageTrigger.disabled = true;
        }
        if (this.#languageTriggerLabel) {
          this.#languageTriggerLabel.textContent = '';
        }
        return;
      }

      for (const language of languages) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'popover__value-option subheading link-faded-reverse';
        button.setAttribute('role', 'option');
        button.setAttribute('data-geo-language', language.isoCode);
        const label = document.createElement('span');
        label.textContent = language.name;
        button.appendChild(label);
        this.#languageList.appendChild(button);
      }

      const matchedLanguage = this.#matchLanguageCode(preferredLanguage, languages);
      this.#setLanguage(matchedLanguage?.isoCode ?? languages[0].isoCode);
    }

    async #init() {
      if (this.#localizationData?.currentCountry) {
        this.#setCountry(this.#localizationData.currentCountry, this.#localizationData.currentLanguage);
      }

      // Theme customizer (admin): skip geo detection. Storefront preview (?preview_theme_id=) runs normally.
      if (Shopify.designMode && !isStorefrontThemePreview()) {
        return;
      }

      if (isDismissed()) {
        return;
      }

      const result = await shouldShowGeoModal(this.#localizationData);
      const { show, country } = result;

      if (!show) {
        return;
      }

      if (!country || !this.#getCountryData(country)) {
        return;
      }

      this.#setCountry(country, null, { useMarketDefaultLanguage: true });
      this.show();
    }

    #onAccept() {
      if (!this.#selectedCountry) return;

      this.#accepted = true;
      setDismissed();

      updateLocalization({
        country: this.#selectedCountry,
        language: this.#languageTrigger?.disabled ? undefined : this.#selectedLanguage,
      });
    }
  }

  window.customElements.define('geo-location-modal', GeoLocationModalElement);
}

function initGeoLocationModal() {
  const modalElement = document.querySelector('geo-location-modal');
  const localizationScript = document.querySelector('[data-geo-localization]');

  if (!modalElement) {
    return;
  }

  const themeModuleUrl = localizationScript?.dataset.themeModule;
  if (!themeModuleUrl) {
    return;
  }

  import(/* @vite-ignore */ themeModuleUrl)
    .then(({ Modal }) => {
      registerGeoLocationModal(Modal);
    })
    .catch(() => {});
}

initGeoLocationModal();
